import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Shield, Bell, Menu, X } from 'lucide-react';
import { ProfileSettings, SecuritySettings, NotificationSettings } from '@/components/Settings';
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const navigationItems = [
  { id: 'profile',       label: 'My Profile',    icon: User   },
  { id: 'security',      label: 'Security',       icon: Shield },
  { id: 'notifications', label: 'Notifications',  icon: Bell   },
];

interface PortalAccountSettingsProps {
  isMobile?: boolean;
}

export const PortalAccountSettings: React.FC<PortalAccountSettingsProps> = ({ isMobile = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return navigationItems.some(n => n.id === tab) ? tab! : 'profile';
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const isGlobalDarkMode = document.documentElement.classList.contains('dark');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && navigationItems.some(n => n.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabClick = (id: string) => {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next);
    setMobileDrawerOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (searchParams.has('modal')) {
        searchParams.delete('modal');
        searchParams.delete('tab');
        setSearchParams(searchParams);
      } else {
        navigate('/dashboard');
      }
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'security':      return <SecuritySettings isGlobalDarkMode={isGlobalDarkMode} />;
      case 'notifications': return <NotificationSettings isGlobalDarkMode={isGlobalDarkMode} />;
      default:              return <ProfileSettings isGlobalDarkMode={isGlobalDarkMode} />;
    }
  };

  const borderColor = 'border-sidebar-border';
  const textColor = 'text-foreground';

  const NavList = () => (
    <ul className="space-y-1">
      {navigationItems.map(item => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;
        return (
          <li key={item.id}>
            <button
              onClick={() => handleTabClick(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                isActive
                  ? isGlobalDarkMode
                    ? 'bg-nav-active text-white shadow-sm border-transparent'
                    : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                  : isGlobalDarkMode
                    ? 'text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span>{item.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );

  if (isMobile) {
    return (
      <Dialog open={true} onOpenChange={handleOpenChange}>
        <DialogContent className={`max-w-md w-full h-[100dvh] p-0 flex flex-col sm:rounded-none ${isGlobalDarkMode ? 'bg-sidebar border-sidebar-border' : 'bg-white'}`}>
          <VisuallyHidden>
            <DialogTitle>Account Settings</DialogTitle>
          </VisuallyHidden>
          <Helmet>
            <title>Account Settings | Client Portal</title>
          </Helmet>

          {/* Mobile Header */}
          <div
            className={`flex-shrink-0 flex items-center justify-between gap-3 px-4 py-4 border-b ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'}`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className={`p-2 rounded-lg transition-colors ${
                  isGlobalDarkMode
                    ? 'text-sidebar-foreground/80 hover:bg-sidebar-accent'
                    : 'text-gray-600 hover:bg-sidebar-light-hover'
                }`}
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className={`text-lg font-semibold ${textColor}`}>Account Settings</h2>
            </div>
            <DialogClose className={`p-2 rounded-lg transition-colors ${
              isGlobalDarkMode
                ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
                : 'hover:bg-sidebar-light-hover text-gray-600'
            }`}>
              <X className="w-5 h-5" />
            </DialogClose>
          </div>

          {/* Mobile Content */}
          <div className="flex-1 overflow-auto">
            <div className="p-4">
              {renderContent()}
            </div>
          </div>

          {/* Mobile Drawer Overlay */}
          {mobileDrawerOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
                onClick={() => setMobileDrawerOpen(false)}
              />
              <aside
                className={`fixed left-0 top-0 w-72 max-w-[85vw] h-[100dvh] border-r flex flex-col z-[70] ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'}`}
              >
                {/* Drawer Header */}
                <div className={`px-5 py-5 border-b ${borderColor} flex items-center justify-between`}>
                  <h3 className={`text-base font-semibold ${textColor}`}>Account Settings</h3>
                  <button
                    onClick={() => setMobileDrawerOpen(false)}
                    className={`p-2 rounded-lg transition-colors ${
                      isGlobalDarkMode
                        ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
                        : 'hover:bg-sidebar-light-hover text-gray-600'
                    }`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Drawer Nav */}
                <nav className="flex-1 p-3 overflow-y-auto">
                  <NavList />
                </nav>
              </aside>
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-w-4xl w-full h-[85vh] p-0 flex flex-row overflow-hidden border ${isGlobalDarkMode ? 'bg-background border-sidebar-border' : 'bg-white border-gray-200'}`}>
        <VisuallyHidden>
          <DialogTitle>Account Settings</DialogTitle>
        </VisuallyHidden>
        <Helmet>
          <title>Account Settings | Client Portal</title>
        </Helmet>

        <DialogClose className={`absolute right-4 top-4 z-50 p-2 rounded-lg transition-colors ${
          isGlobalDarkMode
            ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
            : 'hover:bg-sidebar-light-hover text-gray-600'
        }`}>
          <X className="w-5 h-5" />
        </DialogClose>

        {/* Left Sidebar */}
        <div
          className={`w-64 border-r flex flex-col flex-shrink-0 ${borderColor} ${isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light-secondary'}`}
        >
          {/* Header */}
          <div className={`px-6 py-6 border-b ${borderColor}`}>
            <h2 className={`text-lg font-semibold ${textColor}`}>
              Account Settings
            </h2>
            <p className="text-sm text-muted-foreground mt-1">Manage your profile and preferences</p>
          </div>

          {/* Navigation */}
          <nav className="p-4 flex-1 overflow-y-auto">
            <NavList />
          </nav>
        </div>

        {/* Main Content Area */}
        <div className={`flex-1 overflow-y-auto h-full relative ${isGlobalDarkMode ? 'bg-background' : 'bg-white'}`}>
          <div className="flex justify-center p-8 pb-16">
            <div className="w-full max-w-2xl">
              {renderContent()}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

